import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, MailPlus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { API } from "@/lib/site";

export default function FinalCTA() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setMessage("");
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      setStatus("success");
      setMessage(data.message);
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? "Please enter a valid email address." : "Something went wrong. Please try again.";
      setStatus("error");
      setMessage(msg);
      toast.error(msg);
    }
  };

  return (
    <section className="relative overflow-hidden py-24 lg:py-36" aria-labelledby="final-cta-heading">
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[480px] w-[820px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#2563EB]/22 blur-[140px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <p className="mb-6 text-xs font-semibold uppercase tracking-[0.35em] text-[#00D4FF]">08 — Begin</p>
          <h2 id="final-cta-heading" className="font-display text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl">
            Start Reading With <span className="text-glow-gradient">Confidence</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Experience a smarter, simpler way to read the world around you.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <a
            href="/?view=app"
            data-testid="final-cta-launch-button"
            className="group mt-10 inline-flex min-h-[60px] items-center gap-3 rounded-full bg-[#2563EB] px-10 py-5 text-lg font-bold text-white shadow-[0_0_36px_rgba(37,99,235,0.55)] transition-all duration-300 hover:scale-[1.04] hover:bg-[#1D4ED8] hover:shadow-[0_0_54px_rgba(0,212,255,0.6)]"
          >
            Launch VoiceLens AI
            <ArrowUpRight className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" aria-hidden="true" />
          </a>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="glass-panel mx-auto mt-16 max-w-xl rounded-3xl p-7 sm:p-9" data-testid="newsletter-card">
            <div className="flex items-center justify-center gap-3">
              <MailPlus className="h-6 w-6 text-[#00D4FF]" aria-hidden="true" />
              <h3 className="font-display text-lg font-bold text-white sm:text-xl">Get early access updates</h3>
            </div>
            <p className="mt-2 text-sm text-slate-400 sm:text-base">
              Launch news, new voices, and accessibility tips. No spam, ever.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex flex-col gap-3 sm:flex-row" noValidate={false}>
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="newsletter-email-input"
                className="min-h-[56px] flex-1 rounded-full border border-[#00D4FF]/25 bg-[#050F1F] px-6 text-base text-white placeholder:text-slate-500 focus:border-[#00D4FF]"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="newsletter-submit-button"
                className="min-h-[56px] rounded-full bg-[#00D4FF] px-8 text-base font-bold text-[#071A35] transition-all duration-300 hover:bg-white disabled:opacity-60"
              >
                {status === "loading" ? "Joining…" : "Get Early Access"}
              </button>
            </form>
            <p
              aria-live="polite"
              data-testid="newsletter-status"
              className={`mt-4 min-h-[1.5rem] text-sm font-medium ${status === "success" ? "text-emerald-300" : status === "error" ? "text-rose-300" : "text-slate-500"}`}
            >
              {message}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
